<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, reactive, computed, onMounted } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import nexceoSettingsAPI from 'dashboard/api/nexceo/settings';
import projectGoogleSyncAPI from 'dashboard/api/nexceo/projectGoogleSync';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
/* global axios */

const getters = useStoreGetters();
const currentAccountId = getters.getCurrentAccountId;

const isLoading = ref(false);
const isSaving = ref(false);
const loadError = ref(null);
const saveSuccess = ref(false);
const saveError = ref(null);

const oauthCallbackUri = ref(
  `${window.location.origin}/api/v1/accounts/${currentAccountId.value}/nexceo/google_integration/oauth_callback`
);

const settings = reactive({
  google_oauth_client_id: '',
  google_oauth_client_secret: '',
  google_oauth_credentials_source: 'missing',
  google_oauth_connected: false,
  google_oauth_connected_email: '',
  google_oauth_scope: '',
  google_calendar_enabled: false,
  google_calendar_id: '',
  google_calendar_timezone: '',
  google_calendar_add_meet_for_video_calls: true,
  google_calendar_auth_mode: 'oauth',
  google_calendar_impersonated_user: '',
  google_calendar_credentials: '',
  google_sheets_sync_enabled: false,
  google_sheets_spreadsheet_id: '',
  google_sheets_sheet_name: 'Verified Leads',
  google_sheets_credentials: '',
  google_sheets_sync_mode: 'event_based',
  google_sheets_auth_mode: 'oauth',
  google_sheets_all_leads_sync_enabled: false,
  google_sheets_all_leads_sheet_name: 'All Leads',
  google_sheets_all_leads_sync_mode: 'both',
  google_sheets_inventory_sync_enabled: false,
});

// OAuth
const gOAuthLoading = ref(false);
const gOAuthResult = ref(null);
const gDisconnectLoading = ref(false);
// Calendar test
const gCalTestLoading = ref(false);
const gCalTestResult = ref(null);
const gCalMeetTestLoading = ref(false);
const gCalMeetTestResult = ref(null);
// Sheets test
const gSheetsTestLoading = ref(false);
const gSheetsTestResult = ref(null);
const gSheetsSyncLoading = ref(false);
const gSheetsSyncResult = ref(null);
const gAllLeadsSyncLoading = ref(false);
const gAllLeadsSyncResult = ref(null);
// Inventory sync (M29.5.21 — centralized account-level Inventory Sync)
const inventoryProjects = ref([]);
const isLoadingInventoryProjects = ref(false);
const inventorySyncAllLoading = ref(false);
const inventorySyncAllResult = ref(null);
const inventoryProjectRetryLoading = ref({});

function applySettings(data) {
  const googleKeys = [
    'google_oauth_client_id',
    'google_oauth_client_secret',
    'google_oauth_credentials_source',
    'google_oauth_connected',
    'google_oauth_connected_email',
    'google_oauth_scope',
    'google_calendar_enabled',
    'google_calendar_id',
    'google_calendar_timezone',
    'google_calendar_add_meet_for_video_calls',
    'google_calendar_auth_mode',
    'google_calendar_impersonated_user',
    'google_calendar_credentials',
    'google_sheets_sync_enabled',
    'google_sheets_spreadsheet_id',
    'google_sheets_sheet_name',
    'google_sheets_credentials',
    'google_sheets_sync_mode',
    'google_sheets_auth_mode',
    'google_sheets_all_leads_sync_enabled',
    'google_sheets_all_leads_sheet_name',
    'google_sheets_all_leads_sync_mode',
    'google_sheets_inventory_sync_enabled',
  ];
  googleKeys.forEach(k => {
    if (data[k] === null || data[k] === undefined) return;
    settings[k] = data[k];
  });
  const nullToEmpty = [
    'google_oauth_client_id',
    'google_oauth_client_secret',
    'google_oauth_scope',
    'google_calendar_id',
    'google_calendar_timezone',
    'google_calendar_credentials',
    'google_calendar_impersonated_user',
    'google_sheets_spreadsheet_id',
    'google_sheets_credentials',
  ];
  nullToEmpty.forEach(k => {
    if (!settings[k]) settings[k] = '';
  });
  settings.google_sheets_sheet_name =
    settings.google_sheets_sheet_name || 'Verified Leads';
  settings.google_sheets_all_leads_sheet_name =
    settings.google_sheets_all_leads_sheet_name || 'All Leads';
}

async function load() {
  isLoading.value = true;
  loadError.value = null;
  try {
    const { data } = await nexceoSettingsAPI.get();
    applySettings(data.settings || {});
  } catch {
    loadError.value = 'Failed to load Google settings.';
  } finally {
    isLoading.value = false;
  }
}

async function save() {
  isSaving.value = true;
  saveError.value = null;
  saveSuccess.value = false;
  try {
    const payload = {
      google_oauth_client_id: settings.google_oauth_client_id || null,
      google_calendar_enabled: settings.google_calendar_enabled,
      google_calendar_id: settings.google_calendar_id || null,
      google_calendar_timezone: settings.google_calendar_timezone || null,
      google_calendar_add_meet_for_video_calls:
        settings.google_calendar_add_meet_for_video_calls,
      google_calendar_auth_mode: settings.google_calendar_auth_mode,
      google_calendar_impersonated_user:
        settings.google_calendar_impersonated_user || null,
      google_calendar_credentials: settings.google_calendar_credentials || null,
      google_sheets_sync_enabled: settings.google_sheets_sync_enabled,
      google_sheets_spreadsheet_id:
        settings.google_sheets_spreadsheet_id || null,
      google_sheets_sheet_name:
        settings.google_sheets_sheet_name || 'Verified Leads',
      google_sheets_credentials: settings.google_sheets_credentials || null,
      google_sheets_sync_mode: settings.google_sheets_sync_mode,
      google_sheets_auth_mode: settings.google_sheets_auth_mode,
      google_sheets_all_leads_sync_enabled:
        settings.google_sheets_all_leads_sync_enabled,
      google_sheets_all_leads_sheet_name:
        settings.google_sheets_all_leads_sheet_name || 'All Leads',
      google_sheets_all_leads_sync_mode:
        settings.google_sheets_all_leads_sync_mode,
      google_sheets_inventory_sync_enabled:
        settings.google_sheets_inventory_sync_enabled,
    };
    // Handle masked secret
    if (settings.google_oauth_client_secret === '[masked]') {
      // no-op: keep existing secret
    } else {
      payload.google_oauth_client_secret =
        settings.google_oauth_client_secret || null;
    }
    await nexceoSettingsAPI.update(payload);
    saveSuccess.value = true;
    await load();
    setTimeout(() => {
      saveSuccess.value = false;
    }, 3000);
  } catch (e) {
    saveError.value = e?.response?.data?.error || 'Failed to save settings.';
  } finally {
    isSaving.value = false;
  }
}

async function connectGoogleOAuth() {
  gOAuthLoading.value = true;
  gOAuthResult.value = null;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/nexceo/google_integration/oauth_start`
    );
    if (!data.success) {
      gOAuthResult.value = { success: false, message: data.message };
      return;
    }
    const popup = window.open(
      data.url,
      'nexceo_google_oauth',
      'width=600,height=700'
    );
    const handler = event => {
      if (
        event.data?.type === 'nexceo_google_oauth_success' ||
        event.data?.type === 'nexceo_google_oauth_error'
      ) {
        window.removeEventListener('message', handler);
        if (event.data.type === 'nexceo_google_oauth_success') {
          gOAuthResult.value = {
            success: true,
            message: `Connected as ${event.data.email || 'Google account'}.`,
          };
          load();
        } else {
          gOAuthResult.value = {
            success: false,
            message: event.data.message || 'OAuth failed.',
          };
        }
      }
    };
    window.addEventListener('message', handler);
    setTimeout(() => window.removeEventListener('message', handler), 300000);
    if (!popup)
      gOAuthResult.value = {
        success: false,
        message: 'Popup blocked. Allow popups and try again.',
      };
  } catch (e) {
    gOAuthResult.value = {
      success: false,
      message: e?.response?.data?.message || 'Failed to start OAuth.',
    };
  } finally {
    gOAuthLoading.value = false;
  }
}

async function disconnectGoogleOAuth() {
  gDisconnectLoading.value = true;
  try {
    await axios.delete(
      `/api/v1/accounts/${currentAccountId.value}/nexceo/google_integration/oauth_disconnect`
    );
    gOAuthResult.value = {
      success: true,
      message: 'Google account disconnected.',
    };
    load();
  } catch (e) {
    gOAuthResult.value = {
      success: false,
      message: e?.response?.data?.message || 'Disconnect failed.',
    };
  } finally {
    gDisconnectLoading.value = false;
  }
}

async function testGCalConnection() {
  gCalTestLoading.value = true;
  gCalTestResult.value = null;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/nexceo/google_integration/test_calendar`
    );
    gCalTestResult.value = data;
  } catch (e) {
    gCalTestResult.value = {
      success: false,
      message: e?.response?.data?.message || 'Connection failed.',
    };
  } finally {
    gCalTestLoading.value = false;
  }
}

async function createTestMeet() {
  gCalMeetTestLoading.value = true;
  gCalMeetTestResult.value = null;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/nexceo/google_integration/create_test_meet`
    );
    gCalMeetTestResult.value = data;
  } catch (e) {
    gCalMeetTestResult.value = {
      success: false,
      message: e?.response?.data?.message || 'Test Meet creation failed.',
    };
  } finally {
    gCalMeetTestLoading.value = false;
  }
}

async function testGSheetsConnection() {
  gSheetsTestLoading.value = true;
  gSheetsTestResult.value = null;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/nexceo/google_integration/test_sheets`
    );
    gSheetsTestResult.value = data;
  } catch (e) {
    gSheetsTestResult.value = {
      success: false,
      message: e?.response?.data?.message || 'Connection failed.',
    };
  } finally {
    gSheetsTestLoading.value = false;
  }
}

async function syncGSheetsNow() {
  gSheetsSyncLoading.value = true;
  gSheetsSyncResult.value = null;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/nexceo/google_integration/sync_sheets_now`
    );
    gSheetsSyncResult.value = data;
  } catch (e) {
    gSheetsSyncResult.value = {
      error: e?.response?.data?.error || 'Sync failed.',
    };
  } finally {
    gSheetsSyncLoading.value = false;
  }
}

async function syncAllLeadsNow() {
  gAllLeadsSyncLoading.value = true;
  gAllLeadsSyncResult.value = null;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/nexceo/google_integration/sync_all_leads_now`
    );
    gAllLeadsSyncResult.value = data;
  } catch (e) {
    gAllLeadsSyncResult.value = {
      error: e?.response?.data?.error || 'Sync failed.',
    };
  } finally {
    gAllLeadsSyncLoading.value = false;
  }
}

async function loadInventoryProjects() {
  isLoadingInventoryProjects.value = true;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${currentAccountId.value}/nexceo/google_integration/inventory_projects`
    );
    inventoryProjects.value = data.projects || [];
  } catch {
    inventoryProjects.value = [];
  } finally {
    isLoadingInventoryProjects.value = false;
  }
}

// M29.5.21A — sync_all_inventory now enqueues background jobs and returns
// immediately (running the real sync inline in this request is what caused
// the original "Sync failed" bug: it reliably exceeded both Google's
// per-minute API quota and this app's 15s request timeout for any real
// inventory). There is no final synchronous summary to show any more —
// instead, poll inventory_projects a few times so each row's status badge
// walks SYNCING -> SYNCED/FAILED/PARTIAL FAILURE on its own.
const INVENTORY_POLL_INTERVAL_MS = 4000;
const INVENTORY_POLL_ATTEMPTS = 8;

async function pollInventoryProjectsWhileSyncing() {
  for (let attempt = 0; attempt < INVENTORY_POLL_ATTEMPTS; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise(resolve => {
      setTimeout(resolve, INVENTORY_POLL_INTERVAL_MS);
    });
    // eslint-disable-next-line no-await-in-loop
    await loadInventoryProjects();
    const stillSyncing = inventoryProjects.value.some(
      p => p.sync_status === 'syncing'
    );
    if (!stillSyncing) return;
  }
}

async function syncAllInventoryNow() {
  inventorySyncAllLoading.value = true;
  inventorySyncAllResult.value = null;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/nexceo/google_integration/sync_all_inventory`
    );
    inventorySyncAllResult.value = data;
    await loadInventoryProjects();
    pollInventoryProjectsWhileSyncing();
  } catch (e) {
    inventorySyncAllResult.value = {
      success: false,
      message: e?.response?.data?.message || 'Sync failed to start.',
    };
  } finally {
    inventorySyncAllLoading.value = false;
  }
}

async function retryProjectInventorySync(projectId) {
  inventoryProjectRetryLoading.value = {
    ...inventoryProjectRetryLoading.value,
    [projectId]: true,
  };
  try {
    await projectGoogleSyncAPI.retry(projectId);
    await loadInventoryProjects();
  } catch {
    // Per-project errors surface via the row's status badge on reload.
  } finally {
    inventoryProjectRetryLoading.value = {
      ...inventoryProjectRetryLoading.value,
      [projectId]: false,
    };
  }
}

// M29.5.21A — driven by the backend's computed `sync_status`
// (not_connected/not_provisioned/syncing/synced/partial_failure/failed),
// never the raw google_sheets_sync_status column — a project must never
// visually claim "synced" just because spreadsheet metadata exists.
const SYNC_STATUS_DISPLAY = {
  synced: {
    icon: '✓',
    label: 'Synced',
    class: 'bg-success-50 text-success-700',
  },
  syncing: { icon: '⟳', label: 'Syncing', class: 'bg-amber-50 text-amber-700' },
  partial_failure: {
    icon: '!',
    label: 'Partial failure',
    class: 'bg-amber-50 text-amber-700',
  },
  failed: { icon: '!', label: 'Failed', class: 'bg-ruby-50 text-ruby-700' },
  not_provisioned: {
    icon: '○',
    label: 'Not provisioned',
    class: 'bg-n-alpha-2 text-n-slate-8',
  },
  not_connected: {
    icon: '—',
    label: 'Not connected',
    class: 'bg-n-alpha-2 text-n-slate-8',
  },
};

function syncStatusDisplay(status) {
  return SYNC_STATUS_DISPLAY[status] || SYNC_STATUS_DISPLAY.not_provisioned;
}

function formatLastSync(timestamp) {
  return timestamp ? new Date(timestamp).toLocaleString() : 'Never';
}

const connectionStatusClass = computed(() => {
  if (settings.google_oauth_connected)
    return 'bg-success-50 text-success-700 border-success-200';
  if (settings.google_oauth_credentials_source !== 'missing')
    return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-n-alpha-1 text-n-slate-9 border-n-weak';
});

const connectionStatusText = computed(() => {
  if (settings.google_oauth_connected)
    return `Connected — ${settings.google_oauth_connected_email || 'Google account'}`;
  if (settings.google_oauth_credentials_source === 'env')
    return 'Credentials from ENV — not yet connected';
  if (settings.google_oauth_credentials_source === 'db')
    return 'Credentials saved — not yet connected';
  return 'Not configured';
});

onMounted(() => {
  load();
  loadInventoryProjects();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <SettingsLayout
    :is-loading="isLoading"
    loading-message="Loading Google settings…"
  >
    <template #header>
      <BaseSettingsHeader
        title="Google Integration"
        description="Connect Google Calendar, Meet, and Sheets to NEXCEO. One OAuth connection covers all three services."
        feature-name="integrations"
      />
    </template>
    <template #body>
      <div class="max-w-2xl space-y-6 pb-8">
        <!-- Status banner -->
        <div
          class="flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium"
          :class="connectionStatusClass"
        >
          <span
            class="size-5 shrink-0"
            :class="
              settings.google_oauth_connected
                ? 'i-lucide-check-circle text-success-600'
                : 'i-lucide-alert-circle text-amber-600'
            "
          />
          {{ connectionStatusText }}
        </div>

        <!-- Save feedback -->
        <div
          v-if="saveSuccess"
          class="flex items-center gap-2 px-4 py-3 rounded-xl bg-success-50 border border-success-200 text-success-800 text-sm"
        >
          <span class="i-lucide-check-circle size-4" /> Settings saved.
        </div>
        <div
          v-if="saveError"
          class="flex items-center gap-2 px-4 py-3 rounded-xl bg-ruby-50 border border-ruby-200 text-ruby-700 text-sm"
        >
          <span class="i-lucide-alert-circle size-4" /> {{ saveError }}
        </div>

        <!-- A. OAuth Credentials -->
        <section
          class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
        >
          <div
            class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center justify-between"
          >
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              A. OAuth Credentials
            </h2>
            <span
              class="text-xs px-2 py-0.5 rounded-full font-medium border"
              :class="{
                'bg-success-50 text-success-700 border-success-200':
                  settings.google_oauth_credentials_source === 'env',
                'bg-woot-50 text-woot-700 border-woot-200':
                  settings.google_oauth_credentials_source === 'db',
                'bg-amber-50 text-amber-700 border-amber-200':
                  settings.google_oauth_credentials_source === 'missing',
              }"
            >
              {{
                settings.google_oauth_credentials_source === 'env'
                  ? 'From server ENV'
                  : settings.google_oauth_credentials_source === 'db'
                    ? 'Saved in DB'
                    : 'Not configured'
              }}
            </span>
          </div>
          <div class="px-5 py-4 space-y-3">
            <details class="text-xs text-n-slate-7">
              <summary
                class="cursor-pointer text-woot-600 hover:text-woot-700 font-medium select-none"
              >
                How to get OAuth credentials ▸
              </summary>
              <ol
                class="mt-2 space-y-1 pl-4 list-decimal text-n-slate-8 leading-relaxed"
              >
                <li>
                  Open Google Cloud Console → APIs &amp; Services → Library.
                </li>
                <li>
                  Enable <strong>Google Calendar API</strong> and
                  <strong>Google Sheets API</strong>.
                </li>
                <li>
                  Go to OAuth consent screen → configure app name, scopes.
                </li>
                <li>
                  Go to Credentials → Create OAuth Client ID → Web Application.
                </li>
                <li>
                  Add Authorized redirect URI:
                  <code class="bg-n-alpha-2 px-1 rounded font-mono break-all">{{
                    oauthCallbackUri
                  }}</code>
                </li>
                <li>Copy Client ID and Client Secret below.</li>
              </ol>
            </details>
            <p
              v-if="settings.google_oauth_credentials_source === 'env'"
              class="text-xs text-success-700 flex items-center gap-1"
            >
              <span class="i-lucide-shield-check size-3.5" />
              Server ENV credentials active — fields below are optional DB
              overrides.
            </p>
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-11"
                >OAuth Client ID</label
              >
              <input
                v-model="settings.google_oauth_client_id"
                type="text"
                placeholder="NEXCEO_GOOGLE_OAUTH_CLIENT_ID or enter here"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 font-mono"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-11"
                >OAuth Client Secret</label
              >
              <input
                v-model="settings.google_oauth_client_secret"
                type="password"
                placeholder="NEXCEO_GOOGLE_OAUTH_CLIENT_SECRET or enter here"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 font-mono"
                autocomplete="new-password"
              />
              <p class="text-xs text-n-slate-7">
                Secret is masked after saving. Leave blank to keep existing
                value.
              </p>
            </div>
            <div class="flex justify-end pt-1">
              <button
                :disabled="isSaving"
                class="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
                @click="save"
              >
                <span
                  v-if="isSaving"
                  class="i-lucide-loader-2 size-4 animate-spin"
                />
                Save Credentials
              </button>
            </div>
          </div>
        </section>

        <!-- B. Connected Google Account -->
        <section
          class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
        >
          <div class="px-5 py-3 border-b border-n-weak bg-n-alpha-1">
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              B. Connected Google Account
            </h2>
          </div>
          <div class="px-5 py-4 space-y-3">
            <div
              v-if="settings.google_oauth_connected"
              class="flex items-center justify-between gap-3 p-3 rounded-lg bg-success-50 border border-success-200"
            >
              <div class="flex items-center gap-2">
                <span class="i-lucide-check-circle size-5 text-success-600" />
                <div>
                  <p class="text-sm font-medium text-success-800">Connected</p>
                  <p class="text-xs text-success-700">
                    {{ settings.google_oauth_connected_email }}
                  </p>
                  <p
                    v-if="settings.google_oauth_scope"
                    class="text-xs text-success-600 opacity-75 mt-0.5"
                  >
                    <span
                      v-if="settings.google_oauth_scope.includes('calendar')"
                      >✓ Calendar</span
                    >
                    <span
                      v-if="
                        settings.google_oauth_scope.includes('spreadsheets')
                      "
                    >
                      ✓ Sheets</span
                    >
                  </p>
                </div>
              </div>
              <button
                class="text-xs text-ruby-600 hover:text-ruby-700 px-2.5 py-1.5 rounded-lg border border-ruby-200 hover:bg-ruby-50 transition-colors disabled:opacity-50"
                :disabled="gDisconnectLoading"
                @click="disconnectGoogleOAuth"
              >
                <span
                  v-if="gDisconnectLoading"
                  class="i-lucide-loader-2 size-3.5 animate-spin inline mr-1"
                />
                Disconnect
              </button>
            </div>

            <div v-else class="space-y-2">
              <p class="text-xs text-n-slate-8">
                Connect a Google account to authorize NEXCEO to create Calendar
                events, Meet links, and sync Sheets.
                <strong>Save credentials above first</strong> if you entered
                them in the DB fields.
              </p>
              <button
                class="flex items-center gap-2 px-4 py-2 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
                :disabled="gOAuthLoading"
                @click="connectGoogleOAuth"
              >
                <span
                  :class="
                    gOAuthLoading
                      ? 'i-lucide-loader-2 size-4 animate-spin'
                      : 'i-lucide-link size-4'
                  "
                />
                Connect Google Account
              </button>
            </div>

            <div
              v-if="gOAuthResult"
              class="text-xs rounded-lg px-3 py-2 border"
              :class="
                gOAuthResult.success
                  ? 'text-success-700 bg-success-50 border-success-200'
                  : 'text-ruby-700 bg-ruby-50 border-ruby-200'
              "
            >
              {{ gOAuthResult.message }}
            </div>
          </div>
        </section>

        <!-- C. Calendar / Meet -->
        <section
          class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
        >
          <div class="px-5 py-3 border-b border-n-weak bg-n-alpha-1">
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              C. Calendar / Meet Settings
            </h2>
          </div>
          <div class="px-5 py-4 space-y-3">
            <div
              class="flex items-center justify-between gap-3 py-1 border-b border-n-weak/60"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  Enable Google Calendar Integration
                </p>
                <p class="text-xs text-n-slate-8">
                  Create calendar events for all site visits
                </p>
              </div>
              <button
                class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                :class="
                  settings.google_calendar_enabled
                    ? 'bg-woot-500'
                    : 'bg-n-slate-5'
                "
                @click="
                  settings.google_calendar_enabled =
                    !settings.google_calendar_enabled
                "
              >
                <span
                  class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm"
                  :class="
                    settings.google_calendar_enabled
                      ? 'translate-x-4'
                      : 'translate-x-0.5'
                  "
                />
              </button>
            </div>
            <div class="flex items-center justify-between gap-3 py-1">
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  Add Google Meet for Video Calls
                </p>
                <p class="text-xs text-n-slate-8">
                  Auto-generate a Meet link when visit type is Video Call
                </p>
              </div>
              <button
                class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                :class="
                  settings.google_calendar_add_meet_for_video_calls
                    ? 'bg-woot-500'
                    : 'bg-n-slate-5'
                "
                @click="
                  settings.google_calendar_add_meet_for_video_calls =
                    !settings.google_calendar_add_meet_for_video_calls
                "
              >
                <span
                  class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm"
                  :class="
                    settings.google_calendar_add_meet_for_video_calls
                      ? 'translate-x-4'
                      : 'translate-x-0.5'
                  "
                />
              </button>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1"
                  >Google Calendar ID</label
                >
                <input
                  v-model="settings.google_calendar_id"
                  type="text"
                  placeholder="primary"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
                />
                <p class="text-xs text-n-slate-7 mt-0.5">
                  Use <code>primary</code> for the default calendar.
                </p>
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1"
                  >Default Event Timezone</label
                >
                <input
                  v-model="settings.google_calendar_timezone"
                  type="text"
                  placeholder="Asia/Kolkata"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
                />
              </div>
            </div>
            <div class="flex flex-wrap gap-2 pt-1">
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-n-weak text-n-slate-11 text-xs font-medium hover:bg-n-alpha-2 transition-colors disabled:opacity-50"
                :disabled="gCalTestLoading"
                @click="testGCalConnection"
              >
                <span
                  :class="
                    gCalTestLoading
                      ? 'i-lucide-loader-2 size-3.5 animate-spin'
                      : 'i-lucide-plug size-3.5'
                  "
                />
                Test Calendar Connection
              </button>
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-woot-300 text-woot-600 text-xs font-medium hover:bg-woot-50 transition-colors disabled:opacity-50"
                :disabled="gCalMeetTestLoading"
                @click="createTestMeet"
              >
                <span
                  :class="
                    gCalMeetTestLoading
                      ? 'i-lucide-loader-2 size-3.5 animate-spin'
                      : 'i-lucide-video size-3.5'
                  "
                />
                Create Test Meet Event
              </button>
            </div>
            <div
              v-if="gCalTestResult"
              class="text-xs rounded-lg px-3 py-2 border space-y-1"
              :class="
                gCalTestResult.success
                  ? 'text-success-700 bg-success-50 border-success-200'
                  : 'text-ruby-700 bg-ruby-50 border-ruby-200'
              "
            >
              <span
                :class="
                  gCalTestResult.success
                    ? 'i-lucide-check-circle size-3.5 inline mr-1'
                    : 'i-lucide-x-circle size-3.5 inline mr-1'
                "
              />
              {{ gCalTestResult.message }}
              <strong v-if="gCalTestResult.calendar_summary">
                — {{ gCalTestResult.calendar_summary }}</strong
              >
              <p v-if="gCalTestResult.auth_mode" class="opacity-70 mt-0.5">
                Auth: {{ gCalTestResult.auth_mode }} | Meet capable:
                {{ gCalTestResult.meet_capable ? '✓' : '✗' }}
              </p>
            </div>
            <div
              v-if="gCalMeetTestResult"
              class="text-xs rounded-lg px-3 py-2 border"
              :class="
                gCalMeetTestResult.success
                  ? 'text-success-700 bg-success-50 border-success-200'
                  : 'text-ruby-700 bg-ruby-50 border-ruby-200'
              "
            >
              {{ gCalMeetTestResult.message }}
              <span v-if="gCalMeetTestResult.meeting_url">
                —
                <a
                  :href="gCalMeetTestResult.meeting_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-woot-600 underline"
                  >{{ gCalMeetTestResult.meeting_url }}</a
                >
              </span>
            </div>
            <div class="flex justify-end pt-1">
              <button
                :disabled="isSaving"
                class="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
                @click="save"
              >
                <span
                  v-if="isSaving"
                  class="i-lucide-loader-2 size-4 animate-spin"
                />
                Save Calendar Settings
              </button>
            </div>
          </div>
        </section>

        <!-- D1. Google Sheets — Verified Leads Sync -->
        <section
          class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
        >
          <div class="px-5 py-3 border-b border-n-weak bg-n-alpha-1">
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              D1. Google Sheets — Verified Leads Sync
            </h2>
          </div>
          <div class="px-5 py-4 space-y-3">
            <div
              class="flex items-center justify-between gap-3 py-1 border-b border-n-weak/60"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  Enable Sheets Sync
                </p>
                <p class="text-xs text-n-slate-8">
                  Sync verified leads to Google Sheets in real-time or nightly
                </p>
              </div>
              <button
                class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                :class="
                  settings.google_sheets_sync_enabled
                    ? 'bg-woot-500'
                    : 'bg-n-slate-5'
                "
                @click="
                  settings.google_sheets_sync_enabled =
                    !settings.google_sheets_sync_enabled
                "
              >
                <span
                  class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm"
                  :class="
                    settings.google_sheets_sync_enabled
                      ? 'translate-x-4'
                      : 'translate-x-0.5'
                  "
                />
              </button>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1"
                  >Spreadsheet ID</label
                >
                <input
                  v-model="settings.google_sheets_spreadsheet_id"
                  type="text"
                  placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500"
                />
                <p class="text-xs text-n-slate-7 mt-0.5">
                  From docs.google.com/spreadsheets/d/<strong>[ID]</strong>/edit
                </p>
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1"
                  >Sheet / Tab Name</label
                >
                <input
                  v-model="settings.google_sheets_sheet_name"
                  type="text"
                  placeholder="Verified Leads"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500"
                />
              </div>
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1"
                >Sync Mode</label
              >
              <select
                v-model="settings.google_sheets_sync_mode"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
              >
                <option value="event_based">
                  Event-based (sync on lead verified/updated)
                </option>
                <option value="nightly_full_refresh">
                  Nightly full refresh
                </option>
                <option value="both">
                  Both (event-based + nightly repair)
                </option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1"
                >Sheets Auth Mode</label
              >
              <select
                v-model="settings.google_sheets_auth_mode"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
              >
                <option value="oauth">
                  OAuth Connected Account (Recommended)
                </option>
                <option value="service_account">
                  Service Account (Sheets only, no Meet)
                </option>
              </select>
              <p
                v-if="
                  settings.google_sheets_auth_mode === 'oauth' &&
                  !settings.google_oauth_connected
                "
                class="text-xs text-amber-600 mt-1"
              >
                OAuth mode selected but no Google account connected. Connect one
                in Section B above.
              </p>
            </div>
            <div v-if="settings.google_sheets_auth_mode === 'service_account'">
              <label class="block text-xs font-medium text-n-slate-11 mb-1"
                >Service Account Credentials JSON</label
              >
              <textarea
                v-model="settings.google_sheets_credentials"
                rows="3"
                placeholder="Paste service account JSON or set NEXCEO_GOOGLE_SHEETS_CREDENTIALS_JSON env var."
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm font-mono placeholder:text-n-slate-7 placeholder:font-sans focus:outline-none focus:border-woot-500 resize-none"
              />
            </div>
            <div class="flex flex-wrap gap-2 pt-1">
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-n-weak text-n-slate-11 text-xs font-medium hover:bg-n-alpha-2 transition-colors disabled:opacity-50"
                :disabled="gSheetsTestLoading"
                @click="testGSheetsConnection"
              >
                <span
                  :class="
                    gSheetsTestLoading
                      ? 'i-lucide-loader-2 size-3.5 animate-spin'
                      : 'i-lucide-plug size-3.5'
                  "
                />
                Test Sheets Connection
              </button>
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-woot-500 text-white text-xs font-medium hover:bg-woot-600 transition-colors disabled:opacity-50"
                :disabled="gSheetsSyncLoading"
                @click="syncGSheetsNow"
              >
                <span
                  :class="
                    gSheetsSyncLoading
                      ? 'i-lucide-loader-2 size-3.5 animate-spin'
                      : 'i-lucide-refresh-cw size-3.5'
                  "
                />
                Sync Verified Leads Now
              </button>
            </div>
            <div
              v-if="gSheetsTestResult"
              class="text-xs rounded-lg px-3 py-2 border"
              :class="
                gSheetsTestResult.success
                  ? 'text-success-700 bg-success-50 border-success-200'
                  : 'text-ruby-700 bg-ruby-50 border-ruby-200'
              "
            >
              {{ gSheetsTestResult.message }}
              <span v-if="gSheetsTestResult.spreadsheet_title">
                —
                <strong>{{ gSheetsTestResult.spreadsheet_title }}</strong></span
              >
            </div>
            <div
              v-if="gSheetsSyncResult"
              class="text-xs rounded-lg px-3 py-2 border"
              :class="
                gSheetsSyncResult.error
                  ? 'text-ruby-700 bg-ruby-50 border-ruby-200'
                  : 'text-success-700 bg-success-50 border-success-200'
              "
            >
              <template v-if="gSheetsSyncResult.error">
                <strong>Sync failed:</strong> {{ gSheetsSyncResult.error }}
              </template>
              <template v-else>
                {{
                  gSheetsSyncResult.message ||
                  `Synced ${gSheetsSyncResult.synced} leads.`
                }}
              </template>
            </div>
            <div class="flex justify-end pt-1">
              <button
                :disabled="isSaving"
                class="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
                @click="save"
              >
                <span
                  v-if="isSaving"
                  class="i-lucide-loader-2 size-4 animate-spin"
                />
                Save Sheets Settings
              </button>
            </div>
          </div>
        </section>

        <!-- D2. All Leads Backup Sync -->
        <section
          class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
        >
          <div class="px-5 py-3 border-b border-n-weak bg-n-alpha-1">
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              D2. Google Sheets — All Leads Backup Sync
            </h2>
          </div>
          <div class="px-5 py-4 space-y-3">
            <p class="text-xs text-n-slate-8">
              Sync every lead (all statuses, all sources) to a separate tab in
              the same spreadsheet. Uses the same credentials as Verified Leads.
              Set Spreadsheet ID in D1 first.
            </p>
            <div
              class="flex items-center justify-between gap-3 py-1 border-b border-n-weak/60"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  Enable All Leads Backup Sync
                </p>
                <p class="text-xs text-n-slate-8">
                  Append/update all leads to the All Leads tab on every change
                </p>
              </div>
              <button
                class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                :class="
                  settings.google_sheets_all_leads_sync_enabled
                    ? 'bg-woot-500'
                    : 'bg-n-slate-5'
                "
                @click="
                  settings.google_sheets_all_leads_sync_enabled =
                    !settings.google_sheets_all_leads_sync_enabled
                "
              >
                <span
                  class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm"
                  :class="
                    settings.google_sheets_all_leads_sync_enabled
                      ? 'translate-x-4'
                      : 'translate-x-0.5'
                  "
                />
              </button>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1"
                  >Sheet / Tab Name</label
                >
                <input
                  v-model="settings.google_sheets_all_leads_sheet_name"
                  type="text"
                  placeholder="All Leads"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500"
                />
                <p class="text-xs text-n-slate-7 mt-0.5">
                  Must be a different tab from Verified Leads.
                </p>
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1"
                  >Sync Mode</label
                >
                <select
                  v-model="settings.google_sheets_all_leads_sync_mode"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
                >
                  <option value="event_based">
                    Event-based (sync on lead change)
                  </option>
                  <option value="nightly_full_refresh">
                    Nightly full refresh
                  </option>
                  <option value="both">Both (event-based + nightly)</option>
                </select>
              </div>
            </div>
            <div class="flex flex-wrap gap-2 pt-1">
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-woot-500 text-white text-xs font-medium hover:bg-woot-600 transition-colors disabled:opacity-50"
                :disabled="gAllLeadsSyncLoading"
                @click="syncAllLeadsNow"
              >
                <span
                  :class="
                    gAllLeadsSyncLoading
                      ? 'i-lucide-loader-2 size-3.5 animate-spin'
                      : 'i-lucide-refresh-cw size-3.5'
                  "
                />
                Sync All Leads Now
              </button>
            </div>
            <div
              v-if="gAllLeadsSyncResult"
              class="text-xs rounded-lg px-3 py-2 border"
              :class="
                gAllLeadsSyncResult.error
                  ? 'text-ruby-700 bg-ruby-50 border-ruby-200'
                  : 'text-success-700 bg-success-50 border-success-200'
              "
            >
              <template v-if="gAllLeadsSyncResult.error">
                <strong>Sync failed:</strong> {{ gAllLeadsSyncResult.error }}
              </template>
              <template v-else>
                {{
                  gAllLeadsSyncResult.message ||
                  `All Leads: synced ${gAllLeadsSyncResult.synced ?? 0} rows.`
                }}
              </template>
            </div>
            <div class="flex justify-end pt-1">
              <button
                :disabled="isSaving"
                class="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
                @click="save"
              >
                <span
                  v-if="isSaving"
                  class="i-lucide-loader-2 size-4 animate-spin"
                />
                Save All Leads Settings
              </button>
            </div>
          </div>
        </section>

        <!-- E. Inventory Sync (M29.5.21 — centralized, account-level) -->
        <section
          class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
        >
          <div class="px-5 py-3 border-b border-n-weak bg-n-alpha-1">
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              E. Google (NEXCEO) — Inventory Sync
            </h2>
          </div>
          <div class="px-5 py-4 space-y-3">
            <p class="text-xs text-n-slate-8">
              Automatically synchronize inventory between NEXCEO projects and
              Google Sheets — one spreadsheet per project, one tab per tower.
              Uses the same connected Google account as above. Project-level
              Google Sync configuration has moved here; per-project spreadsheets
              are still managed individually from the table below.
            </p>
            <div class="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
              <span class="flex items-center gap-1.5">
                <span class="text-n-slate-8">Google Account:</span>
                <span
                  :class="
                    settings.google_oauth_connected
                      ? 'text-success-700'
                      : 'text-n-slate-9'
                  "
                  class="font-medium"
                  >{{
                    settings.google_oauth_connected
                      ? 'Connected'
                      : 'Not connected'
                  }}</span
                >
              </span>
              <span class="flex items-center gap-1.5">
                <span class="text-n-slate-8">Inventory Sync:</span>
                <span
                  :class="
                    settings.google_sheets_inventory_sync_enabled
                      ? 'text-success-700'
                      : 'text-n-slate-9'
                  "
                  class="font-medium"
                  >{{
                    settings.google_sheets_inventory_sync_enabled ? 'ON' : 'OFF'
                  }}</span
                >
              </span>
            </div>
            <div
              class="flex items-center justify-between gap-3 py-1 border-b border-n-weak/60"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  Enable Automatic Inventory Sync
                </p>
                <p class="text-xs text-n-slate-8">
                  New projects automatically get a spreadsheet when Google is
                  connected and this is on
                </p>
              </div>
              <button
                class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                :class="
                  settings.google_sheets_inventory_sync_enabled
                    ? 'bg-woot-500'
                    : 'bg-n-slate-5'
                "
                @click="
                  settings.google_sheets_inventory_sync_enabled =
                    !settings.google_sheets_inventory_sync_enabled
                "
              >
                <span
                  class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm"
                  :class="
                    settings.google_sheets_inventory_sync_enabled
                      ? 'translate-x-4'
                      : 'translate-x-0.5'
                  "
                />
              </button>
            </div>
            <p
              v-if="
                settings.google_sheets_inventory_sync_enabled &&
                !settings.google_oauth_connected
              "
              class="text-xs text-amber-600"
            >
              Inventory Sync is on but no Google account is connected yet.
              Connect one in Section B above.
            </p>
            <div class="flex flex-wrap items-center justify-between gap-2 pt-1">
              <button
                :disabled="isSaving"
                class="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
                @click="save"
              >
                <span
                  v-if="isSaving"
                  class="i-lucide-loader-2 size-4 animate-spin"
                />
                Save Inventory Sync Settings
              </button>
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-n-weak text-n-slate-11 text-xs font-medium hover:bg-n-alpha-2 transition-colors disabled:opacity-50"
                :disabled="
                  inventorySyncAllLoading ||
                  !settings.google_sheets_inventory_sync_enabled ||
                  !settings.google_oauth_connected
                "
                @click="syncAllInventoryNow"
              >
                <span
                  :class="
                    inventorySyncAllLoading
                      ? 'i-lucide-loader-2 size-3.5 animate-spin'
                      : 'i-lucide-refresh-cw size-3.5'
                  "
                />
                Sync All Projects Now
              </button>
            </div>
            <div
              v-if="inventorySyncAllResult"
              class="text-xs rounded-lg px-3 py-2 border"
              :class="
                inventorySyncAllResult.success === false
                  ? 'text-ruby-700 bg-ruby-50 border-ruby-200'
                  : 'text-success-700 bg-success-50 border-success-200'
              "
            >
              {{
                inventorySyncAllResult.message ||
                (inventorySyncAllResult.success
                  ? 'Sync started.'
                  : 'Sync failed to start.')
              }}
            </div>

            <div class="pt-2">
              <p
                class="text-xs font-medium text-n-slate-11 uppercase tracking-wide mb-2"
              >
                Projects
              </p>
              <div
                v-if="isLoadingInventoryProjects"
                class="flex items-center justify-center py-8"
              >
                <span
                  class="i-lucide-loader-2 size-5 animate-spin text-woot-500"
                />
              </div>
              <p
                v-else-if="inventoryProjects.length === 0"
                class="text-xs text-n-slate-7"
              >
                No projects yet.
              </p>
              <div
                v-else
                class="border border-n-weak rounded-lg overflow-hidden overflow-x-auto"
              >
                <table class="w-full text-xs">
                  <thead class="bg-n-alpha-1 text-n-slate-8">
                    <tr>
                      <th class="text-left px-3 py-2 font-medium">Project</th>
                      <th class="text-right px-3 py-2 font-medium">Units</th>
                      <th class="text-right px-3 py-2 font-medium">Towers</th>
                      <th class="text-left px-3 py-2 font-medium">
                        Sync Status
                      </th>
                      <th class="text-left px-3 py-2 font-medium">Last Sync</th>
                      <th class="text-left px-3 py-2 font-medium">
                        Spreadsheet
                      </th>
                      <th class="text-right px-3 py-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template
                      v-for="project in inventoryProjects"
                      :key="project.id"
                    >
                      <tr class="border-t border-n-weak/60">
                        <td class="px-3 py-2 text-n-slate-12 whitespace-nowrap">
                          {{ project.name }}
                        </td>
                        <td
                          class="px-3 py-2 text-right tabular-nums text-n-slate-11"
                        >
                          {{ project.unit_count }}
                        </td>
                        <td
                          class="px-3 py-2 text-right tabular-nums text-n-slate-11"
                        >
                          {{ project.tower_count }}
                        </td>
                        <td class="px-3 py-2 whitespace-nowrap">
                          <span
                            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium"
                            :class="
                              syncStatusDisplay(project.sync_status).class
                            "
                          >
                            <span aria-hidden="true">{{
                              syncStatusDisplay(project.sync_status).icon
                            }}</span>
                            {{ syncStatusDisplay(project.sync_status).label }}
                          </span>
                        </td>
                        <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
                          {{
                            formatLastSync(project.google_sheets_last_synced_at)
                          }}
                        </td>
                        <td class="px-3 py-2 whitespace-nowrap">
                          <a
                            v-if="project.google_sheets_spreadsheet_url"
                            :href="project.google_sheets_spreadsheet_url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-woot-600 hover:underline"
                          >
                            Open Sheet
                          </a>
                          <span v-else class="text-n-slate-7">—</span>
                        </td>
                        <td class="px-3 py-2 text-right whitespace-nowrap">
                          <button
                            class="text-woot-600 hover:text-woot-700 font-medium disabled:opacity-50"
                            :disabled="inventoryProjectRetryLoading[project.id]"
                            @click="retryProjectInventorySync(project.id)"
                          >
                            {{
                              inventoryProjectRetryLoading[project.id]
                                ? 'Working…'
                                : project.google_sheets_spreadsheet_id
                                  ? 'Sync Now'
                                  : 'Create Spreadsheet'
                            }}
                          </button>
                        </td>
                      </tr>
                      <tr
                        v-if="
                          ['failed', 'partial_failure'].includes(
                            project.sync_status
                          ) && project.google_sheets_last_sync_error
                        "
                        class="border-t border-n-weak/60"
                      >
                        <td
                          colspan="7"
                          class="px-3 py-2 text-ruby-700 bg-ruby-50/60"
                        >
                          {{ project.name }} —
                          {{ project.google_sheets_last_sync_error }}
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <!-- E. Advanced (service account for Calendar) -->
        <details class="border border-n-weak rounded-xl overflow-hidden">
          <summary
            class="px-5 py-3 bg-n-alpha-1 cursor-pointer flex items-center gap-2 select-none"
          >
            <span class="i-lucide-settings-2 size-3.5 text-n-slate-7" />
            <span
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
              >F. Advanced Calendar Auth (Service Account)</span
            >
            <span
              class="i-lucide-chevron-down size-3.5 text-n-slate-7 ml-auto"
            />
          </summary>
          <div class="px-5 py-4 space-y-3 border-t border-n-weak">
            <p class="text-xs text-amber-700 flex items-start gap-1.5">
              <span class="i-lucide-alert-triangle size-3.5 shrink-0 mt-0.5" />
              Plain service accounts cannot create Google Meet conferences. Use
              OAuth mode for Meet links.
            </p>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1"
                >Calendar Auth Mode</label
              >
              <select
                v-model="settings.google_calendar_auth_mode"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
              >
                <option value="oauth">
                  OAuth — Connected Google Account (Recommended)
                </option>
                <option value="service_account_delegated">
                  Workspace Service Account + Domain-Wide Delegation
                </option>
                <option value="service_account">
                  Plain Service Account (calendar only, no Meet)
                </option>
              </select>
            </div>
            <div
              v-if="
                settings.google_calendar_auth_mode ===
                'service_account_delegated'
              "
            >
              <label class="block text-xs font-medium text-n-slate-11 mb-1"
                >Impersonated User Email</label
              >
              <input
                v-model="settings.google_calendar_impersonated_user"
                type="email"
                placeholder="admin@yourworkspace.com"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
              />
            </div>
            <div v-if="settings.google_calendar_auth_mode !== 'oauth'">
              <label class="block text-xs font-medium text-n-slate-11 mb-1"
                >Service Account Credentials JSON</label
              >
              <textarea
                v-model="settings.google_calendar_credentials"
                rows="3"
                placeholder="Paste service account JSON or set NEXCEO_GOOGLE_CALENDAR_CREDENTIALS_JSON env var."
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm font-mono placeholder:text-n-slate-7 placeholder:font-sans focus:outline-none focus:border-woot-500 resize-none"
              />
            </div>
            <div class="flex justify-end">
              <button
                :disabled="isSaving"
                class="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
                @click="save"
              >
                Save Advanced Settings
              </button>
            </div>
          </div>
        </details>
      </div>
    </template>
  </SettingsLayout>
</template>
